import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  ChamberMemberProAccountAllocationTypeEnum,
  CmpEventPaymentStatusEnum,
  CmpEventRegistrationFieldsFragment,
  CmpEventRegistrationStatusEnum,
  CmpEventSponsorshipRequestStatusEnum,
  useCancelCmpEventRegistrationMutation,
  useCreateCmpEventRegistrationMutation,
  useExportCmpEventAttendeesCsvLazyQuery,
  useGetChamberMemberProSettingsQuery,
  useGetCmpEventDetailQuery,
  useGetCmpEventRegistrationsQuery,
  useGetCmpEventSponsorshipRequestsQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useGetCmpManageEventsQuery,
  useRecordCmpEventRegistrationManualPaymentMutation,
  useUpdateCmpEventRegistrationMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  stickyActionColumnCellSx,
  stickyActionColumnHeaderSx,
} from "../../../common/dashboard/DataTable";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { InvoiceMoneyTextField } from "../../Invoicing/InvoiceMockComponents";
import { CMP_INVOICE_CREATE_LOOKUP_LIMIT } from "../../Invoicing/invoiceLookupConfig";
import {
  AttendeeAssignment,
  AttendeeAssignmentList,
  BillingDetails,
  blackButtonSx,
  blankAttendee,
  blankBillingDetails,
  centsToDollarsString,
  ConfirmDialog,
  cpaGold,
  disabledFieldSx,
  dollarsToCents,
  downloadCsvFile,
  EventSalesFilterBar,
  EventSalesSection,
  EventSalesSortableHeader,
  EventSalesSortState,
  EventSelect,
  EventStatCardRow,
  formatCurrencyFromCents,
  getConfiguredInvoiceTaxRateLabel,
  getConfiguredInvoiceTaxRatePercentage,
  goldButtonSx,
  goldFieldSx,
  InvoiceLineItemTable,
  isComplimentaryEventPaymentStatus as isComplimentaryStatus,
  isPaidEventPaymentStatus as isPaidStatus,
  LineItemDeleteButton,
  ManualPaymentMethodOption,
  MemberBillingSelector,
  MemberLookupOption,
  ProcessPaymentModal,
  ProcessPaymentSubmit,
  redButtonSx,
  ResultModal,
  splitName,
  toggleEventSalesSort,
  UpdateAttendeeNamesModal,
  useDownloadEventInvoicePdf,
} from "./eventSalesShared";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";

type Registration = CmpEventRegistrationFieldsFragment;

type PurchaserMode = "member" | "non-member";

type SortColumn =
  | "attendeeName"
  | "invoiceStatus"
  | "ticketType"
  | "tickets"
  | "ticketStatus";

const ticketStatusLabel = (registration: Registration) =>
  isPaidStatus(registration.paymentStatus) ? "Issued" : "Ordered";

const distinctTicketTypeNames = (registration: Registration) =>
  Array.from(
    new Set(registration.attendees.map((attendee) => attendee.ticketTypeNameSnapshot))
  ).join(", ") || "—";

const activeAttendeeCount = (registration: Registration) =>
  registration.status === CmpEventRegistrationStatusEnum.Canceled
    ? 0
    : registration.attendees.length;

const InvoiceManageTicketSalesContent = (): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const [selectedEventId, setSelectedEventId] = useState("");

  // Page header must match the mock verbatim (route config says
  // "Invoice & Manage Ticket Sales", the mock says otherwise).
  useEffect(() => {
    setPageHeaderOverride({
      title: "Invoice and Manage Event Ticket Sales",
      description:
        "Manually sell event tickets and manage all tickets sales for events",
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride]);

  // ----- Queries -------------------------------------------------------------
  const { data: eventsData, loading: eventsLoading } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const events = useMemo(
    () => eventsData?.getCmpManageEvents.events || [],
    [eventsData]
  );

  useEffect(() => {
    if (!selectedEventId && events.length > 0) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  const { data: eventDetailData } = useGetCmpEventDetailQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const eventDetail = eventDetailData?.getCmpEventDetail.event || null;
  const ticketTypes = useMemo(() => eventDetail?.tickets || [], [eventDetail]);

  const {
    data: registrationsData,
    loading: registrationsLoading,
    refetch: refetchRegistrations,
  } = useGetCmpEventRegistrationsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const registrations = useMemo(
    () => registrationsData?.getCmpEventRegistrations.registrations || [],
    [registrationsData]
  );

  const { data: sponsorshipsData } = useGetCmpEventSponsorshipRequestsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const sponsorships = useMemo(
    () => sponsorshipsData?.getCmpEventSponsorshipRequests.sponsorshipRequests || [],
    [sponsorshipsData]
  );

  // Debounced server-side member search (EVT-BLAIR-SALES-002) — the backend
  // filters on `search` and the limit is raised to 1000 per contract B4.
  // MemberBillingSelector owns the (single) debounce and hands us the trimmed
  // term (the former page-level 300ms double-debounce was removed in the
  // B8/B9 consolidation pass).
  const [memberSearch, setMemberSearch] = useState("");

  const { data: lookupData, loading: lookupsLoading } =
    useGetCmpInvoiceCreateLookupsQuery({
      variables: {
        input: {
          chamberId,
          limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT,
          search: memberSearch || undefined,
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const members: MemberLookupOption[] = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData]
  );

  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const manualPaymentMethods: ManualPaymentMethodOption[] = useMemo(
    () =>
      settingsData?.getChamberMemberProSettings.paymentsAndTaxes
        .manualPaymentMethods || [],
    [settingsData]
  );
  // Tax label + rate come from chamber settings, including split-rate chambers.
  const eventTicketTaxSettings =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes.invoiceTaxSettings
      ?.eventTicketsInvoices;
  const taxRateLabel = getConfiguredInvoiceTaxRateLabel(eventTicketTaxSettings);
  const settingsTaxRatePercentage = getConfiguredInvoiceTaxRatePercentage(
    eventTicketTaxSettings
  );
  // Read-only ACC# auto-populates from the chamber's event ticket sales
  // account allocation.
  const eventTicketAccountNumber =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes.accountAllocations.find(
      (allocation) =>
        allocation.type ===
        ChamberMemberProAccountAllocationTypeEnum.EventTicketSales
    )?.accountNumber || "";

  // ----- Mutations -----------------------------------------------------------
  const [createRegistration, { loading: creating }] =
    useCreateCmpEventRegistrationMutation();
  const [recordPayment, { loading: recordingPayment }] =
    useRecordCmpEventRegistrationManualPaymentMutation();
  const [updateRegistration, { loading: updatingRegistration }] =
    useUpdateCmpEventRegistrationMutation();
  const [cancelRegistration, { loading: cancelingRegistration }] =
    useCancelCmpEventRegistrationMutation();
  const [exportAttendees, { loading: exportingAttendees }] =
    useExportCmpEventAttendeesCsvLazyQuery();
  // Mock's blue "Print PDF" button in the Process Payment modal (reuses the
  // Invoicing backend PDF download).
  const { downloadInvoicePdf, downloadingPdf } =
    useDownloadEventInvoicePdf(chamberId);

  // ----- Analytics -----------------------------------------------------------
  const analytics = useMemo(() => {
    let allocated = 0;
    let paid = 0;
    let unpaid = 0;
    let complimentary = 0;

    registrations.forEach((registration) => {
      const count = activeAttendeeCount(registration);
      if (count === 0) {
        return;
      }
      allocated += count;
      if (isComplimentaryStatus(registration.paymentStatus)) {
        complimentary += count;
      } else if (registration.paymentStatus === CmpEventPaymentStatusEnum.Paid) {
        paid += count;
      } else {
        unpaid += count;
      }
    });

    let sponsorshipTicketsAllocated = 0;
    let sponsorshipTicketsUnpaid = 0;
    sponsorships.forEach((sponsorship) => {
      if (
        sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Canceled ||
        sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Rejected
      ) {
        return;
      }
      allocated += sponsorship.includedAttendeeQuantity;
      sponsorshipTicketsAllocated += sponsorship.includedAttendeeQuantity;
      if (!isPaidStatus(sponsorship.paymentStatus)) {
        sponsorshipTicketsUnpaid += sponsorship.includedAttendeeQuantity;
      }
    });

    return {
      allocated,
      paid,
      unpaid,
      sponsorshipTicketsAllocated,
      sponsorshipTicketsUnpaid,
      complimentary,
      revenueInCents: eventDetail?.ticketRevenueInCents || 0,
    };
  }, [registrations, sponsorships, eventDetail]);

  // ----- Create invoice form -------------------------------------------------
  const [purchaserMode, setPurchaserMode] = useState<PurchaserMode>("member");
  const [billing, setBilling] = useState<BillingDetails>(blankBillingDetails());
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unitPriceDollars, setUnitPriceDollars] = useState("0.00");
  const [taxExempt, setTaxExempt] = useState(false);
  const [attendees, setAttendees] = useState<AttendeeAssignment[]>([blankAttendee()]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pendingCreate, setPendingCreate] = useState<
    "ticket" | "complimentary" | null
  >(null);
  const [resultModal, setResultModal] = useState<{
    title: string;
    message: string;
  } | null>(null);
  // API errors surface in a popup modal (EVT-SALES-T-003) — never a header
  // toast.
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const selectedTicket = ticketTypes.find((ticket) => ticket.id === ticketTypeId);
  const attendeesPerTicket = Math.max(
    1,
    Math.floor(selectedTicket?.attendeesPerTicket || 1)
  );
  const includedAttendeeQuantity = quantity * attendeesPerTicket;
  const taxRatePercentage = taxExempt ? 0 : settingsTaxRatePercentage;

  // If the selected event changes and the chosen ticket type no longer
  // exists, fall back to the "Select Ticket Type" placeholder state.
  useEffect(() => {
    if (ticketTypeId && !ticketTypes.some((t) => t.id === ticketTypeId)) {
      setTicketTypeId("");
      setUnitPriceDollars("0.00");
    }
  }, [ticketTypes, ticketTypeId]);

  // Keep one editable attendee row per admission included in the purchased
  // package quantity (for example, one table ticket creates eight rows).
  useEffect(() => {
    setAttendees((current) => {
      if (includedAttendeeQuantity <= current.length) {
        return current.slice(0, Math.max(1, includedAttendeeQuantity));
      }
      const additions = Array.from(
        { length: includedAttendeeQuantity - current.length },
        () => blankAttendee()
      );
      return [...current, ...additions];
    });
  }, [includedAttendeeQuantity]);

  const unitPriceInCents = dollarsToCents(unitPriceDollars);
  const lineSubtotalInCents = unitPriceInCents * quantity;
  const lineTaxInCents = Math.round((lineSubtotalInCents * taxRatePercentage) / 100);
  const lineTotalInCents = lineSubtotalInCents + lineTaxInCents;

  const clearFieldErrors = (...keys: string[]) =>
    setFieldErrors((current) => {
      if (!keys.some((key) => current[key])) {
        return current;
      }
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const handleBillingChange = (next: BillingDetails) => {
    setBilling(next);
    clearFieldErrors(
      "member",
      "companyName",
      "billingAddress",
      "billingContactName",
      "billingEmail",
      "billingPhone"
    );
  };

  const handlePurchaserModeChange = (mode: PurchaserMode) => {
    setPurchaserMode(mode);
    setBilling((current) => ({ ...current, selectedMemberId: "" }));
    clearFieldErrors("member", "companyName");
  };

  const handleSelectTicketType = (id: string) => {
    setTicketTypeId(id);
    clearFieldErrors("ticketType");
    const ticket = ticketTypes.find((item) => item.id === id);
    if (ticket) {
      setUnitPriceDollars(centsToDollarsString(ticket.priceInCents));
    }
  };

  const handleAttendeesChange = (next: AttendeeAssignment[]) => {
    setAttendees(next);
    clearFieldErrors("attendees");
  };

  // Red trash icon on the line-item row resets the line back to its empty
  // state.
  const handleDeleteLineItem = () => {
    setTicketTypeId("");
    setUnitPriceDollars("0.00");
    setQuantity(1);
    setTaxExempt(false);
    clearFieldErrors("ticketType");
  };

  const resetCreateForm = () => {
    setBilling(blankBillingDetails());
    setQuantity(1);
    setTaxExempt(false);
    setAttendees([blankAttendee()]);
    setTicketTypeId("");
    setUnitPriceDollars("0.00");
    setFieldErrors({});
  };

  // Field-level validation (EVT-SALES-T-003) — errors render in red under the
  // offending field/region. Every included admission needs a name so package
  // sales preserve one attendee record per seat; email remains optional.
  const validateCreate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (purchaserMode === "member") {
      if (!billing.selectedMemberId) {
        errors.member = "Select the member from the drop down.";
      }
    } else if (!billing.customerName.trim()) {
      errors.companyName = "Enter the company or customer name.";
    }
    if (
      !billing.streetAddress.trim() ||
      !billing.city.trim() ||
      !billing.postalCode.trim()
    ) {
      errors.billingAddress =
        "Enter the billing street address, city and postal code.";
    }
    if (!billing.billingContactName.trim()) {
      errors.billingContactName = "Enter the billing contact name.";
    }
    if (!billing.billingEmail.trim()) {
      errors.billingEmail = "Enter the billing contact email.";
    }
    if (!billing.billingPhone.trim()) {
      errors.billingPhone = "Enter the billing phone number.";
    }
    if (!ticketTypeId) {
      errors.ticketType = "Select a ticket type.";
    }
    const rowMissingName = attendees
      .slice(0, includedAttendeeQuantity)
      .some((attendee) => !attendee.firstName.trim());
    if (rowMissingName) {
      errors.attendees =
        "Add a first name for every attendee included with these tickets.";
    }
    return errors;
  };

  const billingErrorList = [
    fieldErrors.member,
    fieldErrors.companyName,
    fieldErrors.billingAddress,
    fieldErrors.billingContactName,
    fieldErrors.billingEmail,
    fieldErrors.billingPhone,
  ].filter(Boolean);

  const handleCreateInvoice = async (complimentary: boolean) => {
    const errors = validateCreate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const selectedMember =
      purchaserMode === "member"
        ? members.find((member) => member.id === billing.selectedMemberId)
        : undefined;
    const purchaser = splitName(billing.billingContactName || billing.customerName);
    const activeAttendees = attendees
      .slice(0, includedAttendeeQuantity);

    setPendingCreate(complimentary ? "complimentary" : "ticket");
    try {
      await createRegistration({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            purchaserFirstName: purchaser.first || billing.customerName,
            purchaserLastName: purchaser.last || "-",
            purchaserEmail: billing.billingEmail,
            purchaserPhone: billing.billingPhone || undefined,
            selectedMemberCompanyId:
              purchaserMode === "member"
                ? selectedMember?.chamberMemberId || undefined
                : undefined,
            selectedMemberCompanyNameSnapshot: billing.customerName || undefined,
            // Non-member purchasers (EVT-BLAIR-SALES-001): billing snapshot per
            // contract B5 (CmpEventPublicBillingInput shape).
            billing:
              purchaserMode === "non-member"
                ? {
                    companyName: billing.customerName || undefined,
                    contactName: billing.billingContactName || undefined,
                    contactEmail: billing.billingEmail || undefined,
                    contactPhone: billing.billingPhone || undefined,
                    streetAddress: billing.streetAddress || undefined,
                    city: billing.city || undefined,
                    postalCode: billing.postalCode || undefined,
                  }
                : undefined,
            ticketQuantities: [{ ticketTypeId, quantity }],
            attendees: activeAttendees.map((attendee) => ({
              firstName: attendee.firstName.trim(),
              lastName: attendee.lastName.trim() || undefined,
              email: attendee.email.trim() || undefined,
              ticketTypeId,
            })),
            invoiceTaxExempt: taxExempt,
            invoiceAccountCode: eventTicketAccountNumber || undefined,
            treatAsComplimentary: complimentary,
          },
        },
      });
      await refetchRegistrations();
      resetCreateForm();
      setResultModal(
        complimentary
          ? {
              title: "Member Complimentary Invoiced",
              message:
                "Complimentary Ticket Invoice has been generated for internal use. Invoice will be marked paid . Ticket will be emailed and show as issued.",
            }
          : {
              title: "Member Ticket(s) Invoiced",
              message:
                "Ticket Invoice has been generated and automatically sent to this member.",
            }
      );
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not create the ticket invoice.",
      });
    } finally {
      setPendingCreate(null);
    }
  };

  // ----- Manage list: filters ------------------------------------------------
  const [search, setSearch] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<
    "all" | "paid" | "unpaid"
  >("all");
  const [ticketTypeFilter, setTicketTypeFilter] = useState("all");
  const [invoiceStatusMenuAnchor, setInvoiceStatusMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [ticketTypeMenuAnchor, setTicketTypeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [sort, setSort] = useState<EventSalesSortState<SortColumn> | null>(
    null
  );

  const filteredRegistrations = useMemo(() => {
    const term = search.trim().toLowerCase();
    return registrations.filter((registration) => {
      if (registration.status === CmpEventRegistrationStatusEnum.Canceled) {
        return false;
      }
      if (term) {
        // Searches BOTH attendee names and company name (EVT-SALES-T-007).
        const haystack = [
          registration.purchaserFirstName,
          registration.purchaserLastName,
          registration.purchaserEmail,
          registration.selectedMemberCompanyNameSnapshot || "",
          ...registration.attendees.map(
            (attendee) => `${attendee.firstName} ${attendee.lastName || ""}`
          ),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) {
          return false;
        }
      }
      if (invoiceStatusFilter === "paid" && !isPaidStatus(registration.paymentStatus)) {
        return false;
      }
      if (
        invoiceStatusFilter === "unpaid" &&
        isPaidStatus(registration.paymentStatus)
      ) {
        return false;
      }
      if (
        ticketTypeFilter !== "all" &&
        !registration.attendees.some(
          (attendee) => attendee.ticketTypeId === ticketTypeFilter
        )
      ) {
        return false;
      }
      return true;
    });
  }, [registrations, search, invoiceStatusFilter, ticketTypeFilter]);

  const sortedRegistrations = useMemo(() => {
    if (!sort) {
      return filteredRegistrations;
    }
    const sortValue = (registration: Registration): string | number => {
      switch (sort.column) {
        case "attendeeName":
          return `${registration.purchaserFirstName} ${registration.purchaserLastName}`.toLowerCase();
        case "invoiceStatus":
          return isPaidStatus(registration.paymentStatus) ? "paid" : "unpaid";
        case "ticketType":
          return distinctTicketTypeNames(registration).toLowerCase();
        case "tickets":
          return registration.attendees.length;
        case "ticketStatus":
          return ticketStatusLabel(registration).toLowerCase();
        default:
          return "";
      }
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filteredRegistrations].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA < valueB) {
        return -1 * factor;
      }
      if (valueA > valueB) {
        return 1 * factor;
      }
      return 0;
    });
  }, [filteredRegistrations, sort]);

  // Shared functional sort header (lifted to eventSalesShared so B9's
  // decorative affordances became real in the same pass).
  const toggleSort = (column: SortColumn) =>
    setSort((current) => toggleEventSalesSort(current, column));

  const renderSortableHeader = (label: string, column: SortColumn) => (
    <EventSalesSortableHeader
      label={label}
      column={column}
      sort={sort}
      onToggle={toggleSort}
    />
  );

  // ----- Process payment (Open Invoice) --------------------------------------
  const [paymentTarget, setPaymentTarget] = useState<Registration | null>(null);

  const handleRecordPayment = async (payload: ProcessPaymentSubmit) => {
    if (!paymentTarget || !paymentTarget.invoice) {
      return;
    }
    try {
      await recordPayment({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            registrationId: paymentTarget.id,
            invoiceId: paymentTarget.invoice.id,
            amountInCents: payload.amountInCents,
            manualPaymentMethodId: payload.manualPaymentMethodId,
            receivedAtUtcMilli: payload.receivedAtUtcMilli,
            reference: payload.reference || undefined,
            note: payload.note || undefined,
          },
        },
      });
      await refetchRegistrations();
      setPaymentTarget(null);
      setResultModal({
        title: "Your payment has been processed successfully!",
        message: "",
      });
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error ? error.message : "Could not process the payment.",
      });
    }
  };

  // ----- Cancel & Void (EVT-BLAIR-SALES-003) ----------------------------------
  const [cancelTarget, setCancelTarget] = useState<Registration | null>(null);

  const handleCancelRegistration = async () => {
    if (!cancelTarget) {
      return;
    }
    try {
      await cancelRegistration({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            registrationId: cancelTarget.id,
          },
        },
      });
      await refetchRegistrations();
      setCancelTarget(null);
      setResultModal({
        title: "Invoice Canceled",
        message:
          "The registration was canceled and its unpaid invoice was voided.",
      });
    } catch (error) {
      setCancelTarget(null);
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not cancel the registration.",
      });
    }
  };

  // ----- Update attendee names -----------------------------------------------
  const [editTarget, setEditTarget] = useState<Registration | null>(null);
  const [editAttendees, setEditAttendees] = useState<
    { id: string; firstName: string; lastName: string; ticketTypeId: string; email: string }[]
  >([]);

  const openEditAttendees = (registration: Registration) => {
    setEditTarget(registration);
    setEditAttendees(
      registration.attendees.map((attendee) => ({
        id: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName || "",
        ticketTypeId: attendee.ticketTypeId,
        email: attendee.email || "",
      }))
    );
  };

  const handleSaveAttendeeNames = async () => {
    if (!editTarget) {
      return;
    }
    try {
      await updateRegistration({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            registrationId: editTarget.id,
            purchaserFirstName: editTarget.purchaserFirstName,
            purchaserLastName: editTarget.purchaserLastName,
            purchaserEmail: editTarget.purchaserEmail,
            purchaserPhone: editTarget.purchaserPhone || undefined,
            selectedMemberCompanyId: editTarget.selectedMemberCompanyId || undefined,
            selectedMemberCompanyNameSnapshot:
              editTarget.selectedMemberCompanyNameSnapshot || undefined,
            status: editTarget.status,
            attendees: editAttendees.map((attendee) => ({
              firstName: attendee.firstName,
              lastName: attendee.lastName || undefined,
              email: attendee.email || undefined,
              ticketTypeId: attendee.ticketTypeId,
            })),
          },
        },
      });
      await refetchRegistrations();
      setEditTarget(null);
      setResultModal({ title: "Attendee Names Updated", message: "" });
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not update attendee names.",
      });
    }
  };

  // ----- Export --------------------------------------------------------------
  const handleExportAttendees = async () => {
    if (!selectedEventId) {
      return;
    }
    const result = await exportAttendees({
      variables: { input: { chamberId, eventId: selectedEventId } },
    });
    const csv = result.data?.exportCmpEventAttendeesCsv;
    if (!csv) {
      if (result.error) {
        setErrorModal({
          title: "Something went wrong",
          message: result.error.message,
        });
      }
      return;
    }
    downloadCsvFile({
      csvContent: csv.csvContent,
      contentType: csv.contentType,
      filename: csv.filename,
    });
  };

  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const eventName = selectedEvent?.title || "Event";

  // POL-021: guard the dirty create-invoice form against route nav and
  // browser Back/Forward. Cleared automatically because resetCreateForm()
  // restores every tracked field after a successful create.
  const createFormDirty =
    Object.values(billing).some((value) => value !== "") ||
    attendees.some(
      (attendee) =>
        attendee.firstName.trim() !== "" ||
        attendee.lastName.trim() !== "" ||
        attendee.email.trim() !== "" ||
        attendee.phone.trim() !== ""
    ) ||
    ticketTypeId !== "" ||
    quantity > 1 ||
    taxExempt;

  // Six icon-less analytics cards + footnotes (EVT-SALES-T-001). Labels and
  // footnotes are mock-verbatim (the "unpaid ," spacing is [sic] in the mock).
  const analyticsCards = [
    {
      label: "Total Tickets Allocated",
      value: String(analytics.allocated),
      helper: eventDetail?.maxAttendeeCapacity
        ? `${analytics.allocated}/${eventDetail.maxAttendeeCapacity}`
        : undefined,
      footnote: "*Includes unpaid , sponsorship and complimentary",
    },
    { label: "Total Tickets Paid", value: String(analytics.paid) },
    { label: "Total Tickets Unpaid", value: String(analytics.unpaid) },
    {
      label: "Sponsorship Tickets Unpaid",
      value: String(analytics.sponsorshipTicketsUnpaid),
      footnote: `*${analytics.sponsorshipTicketsAllocated} sponsorship tickets allocated`,
    },
    {
      label: "Total Complimentary Tickets",
      value: String(analytics.complimentary),
    },
    {
      label: "Total Ticket Revenue",
      value: formatCurrencyFromCents(analytics.revenueInCents),
      footnote: "• Includes paid ticket sales only",
    },
  ];

  return (
    // Binding form-page system (side note 1): FormPageShell provides the
    // 1180px clamp + sr-only heading; the page header override above renders
    // the visible mock-verbatim title. No stepper, no bottom action bar — the
    // mock keeps Invoice Ticket / Invoice Complimentary Ticket inline under
    // the attendee card.
    <FormPageShell
      headingId="ticket-sales-page"
      heading="Invoice and Manage Event Ticket Sales"
      contentClassName="space-y-6"
      beforeMain={<UnsavedChangesPrompt when={createFormDirty} />}
    >
      <EventSalesSection
        id="select-event"
        title="Select Event"
        description="Select event to sell and manage ticket sales."
      >
        <div className="w-full md:w-1/2">
          <EventSelect
            options={events}
            value={selectedEventId}
            onChange={setSelectedEventId}
            loading={eventsLoading}
          />
        </div>
      </EventSalesSection>

      <EventSalesSection
        id="live-analytics"
        title={`${eventName} Live Analytics`}
        description="Few analytics for this event"
      >
        <EventStatCardRow hideIcons cards={analyticsCards} />
      </EventSalesSection>

      <EventSalesSection
        id="create-event-ticket-invoice"
        title="Create Event Ticket Invoice"
        description="Complete the information below, to manually sell member ONLY tickets. Non-members should purchase tickets online. Complimentary tickets will show zero balance on invoice but is required in order to track and assign the ticket."
      >
        <div className="space-y-5">
          <div>
            {/* The card heading/helper render once, INSIDE the bordered card
                (MemberBillingSelector defaults) — the mock shows them in-card
                only; the former duplicate outer h3+p was removed. */}
            <MemberBillingSelector
              members={members}
              billing={billing}
              onChange={handleBillingChange}
              loading={lookupsLoading}
              mode={purchaserMode}
              onModeChange={handlePurchaserModeChange}
              onSearchChange={setMemberSearch}
            />
            {billingErrorList.length > 0 ? (
              <div className="mt-2 space-y-0.5">
                {billingErrorList.map((message) => (
                  <p key={message} className="text-xs font-semibold text-red-600">
                    {message}
                  </p>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="mb-2 text-base font-bold text-gray-950">
              Enter Invoice Details
            </h3>
            <InvoiceLineItemTable>
              <TableCell className="!align-top">
                      <TextField
                        size="small"
                        placeholder="Code"
                        value={selectedTicket?.code || ""}
                        disabled
                        sx={{ width: 90, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={ticketTypeId}
                        onChange={({ target }) =>
                          handleSelectTicketType(target.value)
                        }
                        error={Boolean(fieldErrors.ticketType)}
                        helperText={fieldErrors.ticketType || undefined}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            const selectedId = String(selected || "");
                            const ticket = ticketTypes.find(
                              (item) => item.id === selectedId
                            );
                            if (ticket) {
                              return ticket.name;
                            }
                            return (
                              <span className="text-[#8F969E]">
                                Select Ticket Type
                              </span>
                            );
                          },
                        }}
                        sx={goldFieldSx}
                      >
                        <MenuItem value="" disabled>
                          Select Ticket Type
                        </MenuItem>
                        {ticketTypes.map((ticket) => (
                          <MenuItem value={ticket.id} key={ticket.id}>
                            {ticket.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell className="!align-top">
                      <div className="inline-flex items-center rounded-full border border-gray-300 bg-white">
                        <IconButton
                          size="small"
                          aria-label="decrease quantity"
                          disabled={quantity <= 1}
                          onClick={() =>
                            setQuantity((current) => Math.max(1, current - 1))
                          }
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <span className="w-8 text-center text-sm font-semibold text-gray-950">
                          {quantity}
                        </span>
                        <IconButton
                          size="small"
                          aria-label="increase quantity"
                          onClick={() => setQuantity((current) => current + 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        size="small"
                        placeholder="ACC#"
                        value={eventTicketAccountNumber}
                        disabled
                        sx={{ width: 90, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      {/* POL-005: '$' is an adornment, value stays the
                          canonical decimal-dollars string (B5's shared money
                          cell, disabled — dollarsToCents submit path
                          unchanged). */}
                      <InvoiceMoneyTextField
                        valueInCents={unitPriceInCents}
                        onValueInCentsChange={() => undefined}
                        disabled
                        ariaLabel="Unit price"
                        sx={{ width: 110, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        select
                        size="small"
                        value={taxExempt ? "exempt" : "tax"}
                        onChange={({ target }) =>
                          setTaxExempt(target.value === "exempt")
                        }
                        sx={{ width: 150, ...goldFieldSx }}
                      >
                        <MenuItem value="tax">
                          {taxRateLabel} ({settingsTaxRatePercentage}%)
                        </MenuItem>
                        <MenuItem value="exempt">Tax Exempt</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell className="!align-top">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-950">
                          {formatCurrencyFromCents(lineTotalInCents)}
                        </span>
                        <LineItemDeleteButton onClick={handleDeleteLineItem} />
                      </div>
                    </TableCell>
            </InvoiceLineItemTable>
          </div>

          <div className="rounded border border-gray-200 bg-white p-4">
            <h3 className="text-base font-bold text-gray-950">
              Add attendee details below • {includedAttendeeQuantity}{" "}
              <span className="font-normal text-gray-700">
                {includedAttendeeQuantity === 1 ? "attendee" : "attendees"}{" "}
                included across {quantity}{" "}
                {quantity === 1 ? "ticket package" : "ticket packages"}.
              </span>
            </h3>
            <div className="mt-3">
              <AttendeeAssignmentList
                attendees={attendees.slice(0, includedAttendeeQuantity)}
                onChange={handleAttendeesChange}
                attendeeFieldsOptional
                attendeeNameRequired
              />
            </div>
            {fieldErrors.attendees ? (
              <p className="mt-2 text-xs font-semibold text-red-600">
                {fieldErrors.attendees}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-start gap-6">
            <div>
              <Button
                variant="contained"
                disabled={creating}
                onClick={() => handleCreateInvoice(false)}
                startIcon={
                  creating && pendingCreate === "ticket" ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : undefined
                }
                sx={goldButtonSx}
              >
                Invoice Ticket
              </Button>
              <p className="mt-1 text-xs font-bold text-gray-950">
                Invoice will be generated and sent.
              </p>
            </div>
            <div>
              <Button
                variant="contained"
                disabled={creating}
                onClick={() => handleCreateInvoice(true)}
                startIcon={
                  creating && pendingCreate === "complimentary" ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : undefined
                }
                sx={goldButtonSx}
              >
                Invoice Complimentary Ticket
              </Button>
              <p className="mt-1 text-xs font-bold text-gray-950">
                Invoice will be generated for internal use only and not sent.
              </p>
            </div>
          </div>
        </div>
      </EventSalesSection>

      {/* Export lives in its OWN block after the ticket-purchase area
          (EVT-SALES-T-005), right-aligned. */}
      <div className="flex justify-end">
        <Button
          variant="contained"
          startIcon={<FileUploadOutlinedIcon />}
          disabled={exportingAttendees || !selectedEventId}
          onClick={handleExportAttendees}
          sx={goldButtonSx}
        >
          Export Attendee List
        </Button>
      </div>

      <EventSalesSection
        id="ticket-sale-filters"
        title="Ticket Sale Filters"
        description="Use filters to easily sort information"
      >
        <EventSalesFilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by Attendee Name or Company Name"
          filters={[
            {
              label: "Filter by Invoice Status",
              active: invoiceStatusFilter !== "all",
              onOpen: setInvoiceStatusMenuAnchor,
            },
            {
              label: "Filter by Ticket Type",
              active: ticketTypeFilter !== "all",
              onOpen: setTicketTypeMenuAnchor,
            },
          ]}
          onClearAllFilters={() => {
            setSearch("");
            setInvoiceStatusFilter("all");
            setTicketTypeFilter("all");
          }}
        />

        <Menu
          anchorEl={invoiceStatusMenuAnchor}
          open={Boolean(invoiceStatusMenuAnchor)}
          onClose={() => setInvoiceStatusMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
        >
          {[
            { value: "all", label: "All Invoice Statuses" },
            { value: "paid", label: "Paid" },
            { value: "unpaid", label: "Unpaid" },
          ].map((option) => (
            <MenuItem
              key={option.value}
              selected={invoiceStatusFilter === option.value}
              onClick={() => {
                setInvoiceStatusFilter(option.value as "all" | "paid" | "unpaid");
                setInvoiceStatusMenuAnchor(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={ticketTypeMenuAnchor}
          open={Boolean(ticketTypeMenuAnchor)}
          onClose={() => setTicketTypeMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
        >
          <MenuItem
            selected={ticketTypeFilter === "all"}
            onClick={() => {
              setTicketTypeFilter("all");
              setTicketTypeMenuAnchor(null);
            }}
          >
            All Ticket Types
          </MenuItem>
          {ticketTypes.map((ticket) => (
            <MenuItem
              key={ticket.id}
              selected={ticketTypeFilter === ticket.id}
              onClick={() => {
                setTicketTypeFilter(ticket.id);
                setTicketTypeMenuAnchor(null);
              }}
            >
              {ticket.name}
            </MenuItem>
          ))}
        </Menu>
      </EventSalesSection>

      <EventSalesSection
        id="manage-ticket-list"
        title="Manage Ticket List"
        description={
          <>
            Update ticket invoices status and update attendee names. Only
            ticket invoices marked paid will show status as issued. Unpaid
            ticket invoices will show ticket status as ordered.{" "}
            <span className="font-bold text-gray-950">
              To update ticket invoice open invoice and click paid. and tickets
              will be issued.
            </span>
          </>
        }
      >
        <TableContainer className="rounded border border-gray-200">
          <Table size="small" sx={{ minWidth: 980 }}>
            <TableHead className="bg-gray-50">
              <TableRow>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Attendee Name", "attendeeName")}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Update Invoice Status", "invoiceStatus")}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Ticket Type", "ticketType")}
                </TableCell>
                <TableCell className="!font-bold" align="center">
                  {renderSortableHeader("No. of Tickets", "tickets")}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Ticket Status", "ticketStatus")}
                </TableCell>
                {/* Actions is not sortable (mock) — no sort affordance. */}
                <TableCell
                  className="!font-bold"
                  align="right"
                  sx={{
                    ...stickyActionColumnHeaderSx,
                    minWidth: 280,
                    width: 280,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registrationsLoading && registrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="!py-8">
                    <CircularProgress size={22} sx={{ color: cpaGold }} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!registrationsLoading && sortedRegistrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="!py-8 text-gray-500">
                    No ticket invoices for this event yet.
                  </TableCell>
                </TableRow>
              ) : null}
              {sortedRegistrations.map((registration) => {
                const paid = isPaidStatus(registration.paymentStatus);
                return (
                  <TableRow key={registration.id}>
                    <TableCell>
                      <div className="font-semibold text-gray-900">
                        {registration.purchaserFirstName}{" "}
                        {registration.purchaserLastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {registration.selectedMemberCompanyNameSnapshot ||
                          registration.purchaserEmail}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        {paid ? (
                          <span className="text-sm font-bold text-green-600">
                            Paid
                          </span>
                        ) : (
                          <span className="text-sm font-bold text-gray-950">
                            Unpaid
                          </span>
                        )}
                        {registration.invoice ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setPaymentTarget(registration)}
                            sx={blackButtonSx}
                          >
                            Open Invoice
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{distinctTicketTypeNames(registration)}</TableCell>
                    <TableCell align="center">
                      {registration.attendees.length}
                    </TableCell>
                    <TableCell>{ticketStatusLabel(registration)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...stickyActionColumnCellSx,
                        minWidth: 280,
                        width: 280,
                      }}
                    >
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => openEditAttendees(registration)}
                          sx={goldButtonSx}
                        >
                          Update Attendee Names
                        </Button>
                        {/* Contextual-action matrix (ACTION_MENU_MATRIX
                            'Ticket / Sponsorship Sales'): Cancel & Void is
                            HIDDEN on paid/complimentary rows, not disabled. */}
                        {paid ? null : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setCancelTarget(registration)}
                            sx={redButtonSx}
                          >
                            Cancel &amp; Void Invoice
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </EventSalesSection>

      {/* Process payment (Open Invoice) */}
      <ProcessPaymentModal
        open={Boolean(paymentTarget)}
        customerName={
          paymentTarget
            ? paymentTarget.selectedMemberCompanyNameSnapshot ||
              `${paymentTarget.purchaserFirstName} ${paymentTarget.purchaserLastName}`
            : ""
        }
        invoiceNumber={paymentTarget?.invoice?.invoiceNumber || ""}
        amountDueInCents={paymentTarget?.invoice?.amountDueInCents || 0}
        invoiceLineItems={paymentTarget?.invoice?.lineItems || []}
        subtotalAmountInCents={paymentTarget?.invoice?.subtotalAmountInCents}
        taxAmountInCents={paymentTarget?.invoice?.taxAmountInCents}
        totalAmountInCents={paymentTarget?.invoice?.totalAmountInCents}
        paymentMethods={manualPaymentMethods}
        loading={recordingPayment}
        onSubmit={handleRecordPayment}
        onClose={() => setPaymentTarget(null)}
        onPrintPdf={
          paymentTarget?.invoice
            ? () => downloadInvoicePdf(paymentTarget.invoice?.id)
            : undefined
        }
        printLoading={downloadingPdf}
      />

      {/* Cancel & Void confirm (red-? confirm modal pattern) */}
      <ConfirmDialog
        open={Boolean(cancelTarget)}
        tone="danger"
        title="Are you sure you want to cancel and void this invoice?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        loading={cancelingRegistration}
        onConfirm={handleCancelRegistration}
        onCancel={() => setCancelTarget(null)}
      />

      {/* Update attendee names (shared modal, "labeled" variant preserves
          this page's existing DOM) */}
      <UpdateAttendeeNamesModal
        open={Boolean(editTarget)}
        title="Update Attendee Names"
        attendees={editAttendees}
        onAttendeeChange={(id, patch) =>
          setEditAttendees((current) =>
            current.map((item) =>
              item.id === id ? { ...item, ...patch } : item
            )
          )
        }
        onClose={() => setEditTarget(null)}
        onSave={handleSaveAttendeeNames}
        saving={updatingRegistration}
        variant="labeled"
        fieldSx={goldFieldSx}
      />

      <ResultModal
        open={Boolean(resultModal)}
        title={resultModal?.title || ""}
        message={resultModal?.message}
        onClose={() => setResultModal(null)}
      />

      {/* API errors surface here as a popup, never a header toast. */}
      <ResultModal
        open={Boolean(errorModal)}
        success={false}
        title={errorModal?.title || ""}
        message={errorModal?.message}
        onClose={() => setErrorModal(null)}
      />
    </FormPageShell>
  );
};

const InvoiceManageTicketSales = (): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "ticket-sales"
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return <InvoiceManageTicketSalesContent />;
};

export default InvoiceManageTicketSales;
